import { FetchResult } from '@apollo/client'
import { loader } from 'graphql.macro'
import { client } from 'components/ApolloProvider'
import { Agency } from 'types/agency'
import { Disposition, DispositionCorrection } from 'types/disposition'
import { GET_DISPOSITIONS } from './getDispositions'
import {
  CorrectionMutationResponse,
  GetDispositionsAQR,
  InvoiceMutationResponse,
  UpsertCorrectionVariables,
} from './types'

const dispositionsApi = {
  getDispositions: (payload: {
    agencyId: Agency['id']
    first?: number
    last?: number
  }): Promise<GetDispositionsAQR> =>
    client.query({
      query: GET_DISPOSITIONS,
      variables: {
        agencyId: payload.agencyId,
        first: payload.first ?? null,
        last: payload.last ?? null,
      },
    }),

  upsertCorrection: (
    mutationVariable: UpsertCorrectionVariables
  ): Promise<FetchResult<{ upsertDispositionCorrection: CorrectionMutationResponse }>> =>
    client.mutate({
      mutation: loader('./upsertDispositionCorrection.gql'),
      variables: {
        dispositionId: mutationVariable.dispositionId,
        correctionId: mutationVariable.correctionId ?? null,
        percentChange: mutationVariable.percentChange,
        correctedPrice: mutationVariable.correctedPrice,
        correctionReason: mutationVariable.correctionReason,
      },
    }),

  sendCorrection: (
    correctionId: DispositionCorrection['id']
  ): Promise<FetchResult<{ sendDispositionCorrection: CorrectionMutationResponse }>> =>
    client.mutate({
      mutation: loader('./sendDispositionCorrection.gql'),
      variables: { correctionId },
    }),

  attachInvoice: (
    dispositionId: Disposition['id'],
    file: File
  ): Promise<FetchResult<{ attachDispositionInvoice: InvoiceMutationResponse }>> =>
    client.mutate({
      mutation: loader('./attachDispositionInvoice.gql'),
      variables: { dispositionId, file },
    }),

  sendInvoice: (
    dispositionId: Disposition['id']
  ): Promise<FetchResult<{ sendDispositionInvoice: InvoiceMutationResponse }>> =>
    client.mutate({
      mutation: loader('./sendDispositionInvoice.gql'),
      variables: { dispositionId },
    }),

  removeInvoice: (
    dispositionId: Disposition['id']
  ): Promise<FetchResult<{ removeDispositionInvoice: InvoiceMutationResponse }>> =>
    client.mutate({
      mutation: loader('./removeDispositionInvoice.gql'),
      variables: { dispositionId },
    }),

  attachCorrectionInvoice: (
    correctionId: DispositionCorrection['id'],
    file: File
  ): Promise<FetchResult<{ attachDispositionCorrectionInvoice: CorrectionMutationResponse }>> =>
    client.mutate({
      mutation: loader('./attachDispositionCorrectionInvoice.gql'),
      variables: { correctionId, file },
    }),

  sendCorrectionInvoice: (
    correctionId: DispositionCorrection['id']
  ): Promise<FetchResult<{ sendDispositionCorrectionInvoice: CorrectionMutationResponse }>> =>
    client.mutate({
      mutation: loader('./sendDispositionCorrectionInvoice.gql'),
      variables: { correctionId },
    }),

  removeCorrectionInvoice: (
    correctionId: DispositionCorrection['id']
  ): Promise<FetchResult<{ removeDispositionCorrectionInvoice: CorrectionMutationResponse }>> =>
    client.mutate({
      mutation: loader('./removeDispositionCorrectionInvoice.gql'),
      variables: { correctionId },
    }),
}

export default dispositionsApi
